import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';

import type { Project } from '@/features/projects/api/projects';
import type { QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

export type DocumentsData = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.projects>['documents']['get']>>['data']
>;

export type Document = DocumentsData[number];

type DocumentsQueryOptions = {
  queryConfig?: QueryConfig<typeof getDocuments>;
  id: Project['id'];
  order: 'asc' | 'desc';
};

const getDocuments = (id: Project['id'], order: 'asc' | 'desc') => {
  return extractData(
    api.projects({ id }).documents.get({ query: { sorting: { field: 'updatedAt', order } } })
  );
};

export const getDocumentsQueryOptions = (id: Project['id'], order: 'asc' | 'desc') => {
  return queryOptions({
    queryKey: ['documents', id, order],
    queryFn: () => getDocuments(id, order),
  });
};

export const getDocumentOptions = (id: Project['id'], order: 'asc' | 'desc') => ({
  queryKey: ['documents', id, order],
  queryFn: () => getDocuments(id, order),
});

export const ensureDocumentQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getDocumentsQueryOptions(params.projectId, 'asc'));
};

export const useDocuments = ({ queryConfig, id, order }: DocumentsQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getDocumentsQueryOptions(id, order) });
};
